import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { DepartmentInput } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { TwoColumnLayoutService } from 'src/app/common-components/column-layouts/two-column-list-layout/services/selected-id.service';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { Privileges } from 'src/app/privileges/privileges';
import { createDepartmentFailure } from '../../add-department/state/add-department.actions';
import { MergeDepartmentFormData } from '../../department-register/models/merge-department-form-data';
import { ModifyDepartmentFormData } from '../../department-register/models/modify-department-form-data';
import { departmentRegisterAdapterActions } from '../../department-register/state/department-register.actions';
import { MergeDepartmentComponent } from '../../merge-department/merge-department.component';
import { ModifyDepartmentComponent } from '../../modify-department/modify-department.component';
import { DepartmentDetailSaveModel } from '../models/department-detail-save.model';
import { getDepartmentDetail } from '../queries/department-detail.graphql';
import { GetDepartmentDetail, GetDepartmentDetailVariables } from '../queries/generated/GetDepartmentDetail';
import { DepartmentDetailApiService } from '../services/department-detail-api.service';
import * as DepartmentDetailActions from './department-detail.actions';
@Injectable()
export class DepartmentDetailEffects {
   loadDepartmentDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            DepartmentDetailActions.departmentDetailLoad,
            DepartmentDetailActions.departmentDetailCancel,
            DepartmentDetailActions.updateDepartmentId
         ),
         withLatestFrom(this.twoColumnLayoutService.selectedId$, this.twoColumnLayoutService.firstItemId$),
         switchMap(([action, selectedId, firstItemId]) => {
            if (selectedId === null) {
               // If no selected Id, call loadPageNoDepartment (sets callState to loaded so loading overlay stops displaying spinner)
               return of(DepartmentDetailActions.loadPageNoDepartment());
            }
            // call setShouldShowDetailView to show detail view if firstItemId not null
            this.twoColumnLayoutService.setShouldShowDetailView(firstItemId !== null);

            // set departmentId to selectedId to use in query below
            const departmentId = selectedId;

            return this.apiService
               .query<GetDepartmentDetail, GetDepartmentDetailVariables>({
                  query: getDepartmentDetail,
                  variables: { id: departmentId },
               })
               .pipe(
                  withLatestFrom(
                     this.store.pipe(select(selectPrivileges)),
                     this.store.pipe(select(selectCurrentRoute))
                  ),
                  map(([departmentResult, privileges, route]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManageDepartments_View,
                           departmentResult.data.department.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return DepartmentDetailActions.departmentDetailAccessDenied();
                     }
                     return DepartmentDetailActions.loadDepartmentDetailSuccess({
                        data: departmentResult.data.department,
                     });
                  }),
                  catchError((error) => {
                     return of(DepartmentDetailActions.loadDepartmentDetailFailure(error));
                  })
               );
         })
      );
   });

   deleteDepartment = createEffect(() => {
      return this.actions$.pipe(
         ofType(DepartmentDetailActions.deleteDepartment),
         switchMap((action) => {
            const message = 'Are you sure you would like to delete this Department?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Department?',
                  message,
               },
               size: DialogSize.Medium,
               disableClose: true,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.departmentDetailApiService.deleteDepartment(action.departmentId).pipe(
                        map(() => {
                           return DepartmentDetailActions.modifyDepartmentSuccess({
                              message: `Department ${action.name} Deleted`,
                           });
                        }),
                        catchError((error) => {
                           return of(DepartmentDetailActions.deleteDepartmentFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deactivateDepartment$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DepartmentDetailActions.deactivateDepartment),
         switchMap((action) => {
            const isDeactivating = action.department.isActive === true;
            const message = isDeactivating
               ? 'Are you sure you would like to deactivate this Department?'
               : 'Are you sure you would like to activate this Department?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: isDeactivating ? 'Deactivate Department?' : 'Activate Department?',
                  message,
               },
               size: DialogSize.Medium,
               disableClose: true,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     const departmentToSave: DepartmentDetailSaveModel = {
                        departmentId: action.department.departmentId,
                        name: action.department.name,
                        isActive: !action.department.isActive,
                     };
                     const fields: (keyof DepartmentInput)[] = ['departmentId', 'isActive'];
                     return this.departmentDetailApiService.save(departmentToSave, fields).pipe(
                        map((result) => {
                           this.twoColumnLayoutService.itemDeleted();
                           const snackBarMessage = isDeactivating
                              ? `Department ${action.department.name} Deactivated`
                              : `Department ${action.department.name} Reactivated`;
                           return DepartmentDetailActions.modifyDepartmentSuccess({
                              message: snackBarMessage,
                           });
                        }),
                        catchError((error) => {
                           return of(DepartmentDetailActions.saveDepartmentDetailFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   renameDepartment$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DepartmentDetailActions.renameDepartment),
         switchMap((action) => {
            return this.dialogService
               .open<ModifyDepartmentComponent, ModifyDepartmentFormData>(ModifyDepartmentComponent, {
                  data: { departmentId: action.departmentId, departmentName: action.name },
                  panelClass: [DialogSize.Medium, 'dialog-container'],
                  disableClose: true,
               })
               .afterClosed()
               .pipe(
                  switchMap((result) => {
                     if (result) {
                        return of(
                           DepartmentDetailActions.renameDepartmentConfirmed({
                              ...result,
                              departmentId: action.departmentId,
                           })
                        );
                     } else {
                        return EMPTY;
                     }
                  })
               );
         })
      );
   });

   renameDepartmentConfirmed$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DepartmentDetailActions.renameDepartmentConfirmed),
         switchMap((action) => {
            const departmentToSave: DepartmentDetailSaveModel = {
               departmentId: action.departmentId,
               name: action.name,
               isActive: action.isActive,
            };
            const fields: (keyof DepartmentInput)[] = ['departmentId', 'name'];
            return this.departmentDetailApiService.save(departmentToSave, fields).pipe(
               map((executionResult) => {
                  return DepartmentDetailActions.modifyDepartmentSuccess({
                     message: `Department ${action.name} Saved`,
                  });
               }),
               catchError((error) => {
                  return of(createDepartmentFailure(error));
               })
            );
         })
      );
   });

   mergeDepartment$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DepartmentDetailActions.mergeDepartment),
         switchMap((action) => {
            return this.dialogService
               .open<MergeDepartmentComponent, MergeDepartmentFormData>(MergeDepartmentComponent, {
                  data: {
                     sourceDepartmentId: action.departmentId,
                     sourceDepartmentName: action.name,
                     destinationDepartmentId: null,
                     accountId: action.accountId,
                  },
                  panelClass: [DialogSize.Medium, 'dialog-container'],
                  disableClose: true,
               })
               .afterClosed()
               .pipe(
                  switchMap((result) => {
                     if (result) {
                        return of(
                           DepartmentDetailActions.mergeDepartmentConfirmed({
                              ...result,
                           })
                        );
                     } else {
                        return of(DepartmentDetailActions.renameDepartmentCanceled());
                     }
                  })
               );
         })
      );
   });

   mergeDepartmentConfirmed$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DepartmentDetailActions.mergeDepartmentConfirmed),
         switchMap((action) => {
            return this.departmentDetailApiService
               .mergeDepartments(action.sourceDepartmentId, action.destinationDepartmentId)
               .pipe(
                  map(() => DepartmentDetailActions.modifyDepartmentSuccess({ message: 'Department Merged' })),

                  catchError((error) => {
                     return of(createDepartmentFailure(error));
                  })
               );
         })
      );
   });

   untagDepartment$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(DepartmentDetailActions.untagDepartment),
         switchMap((action) => {
            const message = `This will remove ${action.name} from all associated extensions.`;
            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Untag Department?',
                  message,
               },
               size: DialogSize.Medium,
               disableClose: true,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.departmentDetailApiService.untagDepartment(action.departmentId).pipe(
                        map(() => DepartmentDetailActions.modifyDepartmentSuccess({ message: 'Department Untagged' })),
                        catchError((error) => {
                           return of(DepartmentDetailActions.saveDepartmentDetailFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   modifyDepartmentSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(DepartmentDetailActions.modifyDepartmentSuccess),
            tap((action) => {
               this.dialogService.closeFormComponent();
               this.store.dispatch(departmentRegisterAdapterActions.quickFilterChanged({ quickFilters: [] }));
               this.twoColumnLayoutService.itemDeleted();
               this.snackBar.open(action.message);
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private confirmationService: ConfirmationDialogService,
      private store: Store<AppState>,
      private departmentDetailApiService: DepartmentDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private dialogService: LcsDialogService,
      private location: Location,
      private twoColumnLayoutService: TwoColumnLayoutService
   ) {}
}
